/**
 * MCP (Model Context Protocol) 客户端
 * 用于与后端MCP服务通信
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const MCP_BASE_URL = `${API_BASE_URL}/api/mcp`;

class MCPClient {
  constructor() {
    this.baseURL = MCP_BASE_URL;
    this.timeout = 30000; // 30秒超时
  }

  /**
   * 获取认证头部
   */
  getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  /**
   * 通用MCP请求方法
   */
  async request(url, options = {}) {
    const response = await fetch(`${this.baseURL}${url}`, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers
      },
      timeout: this.timeout
    });

    if (!response.ok) {
      throw new Error(`MCP request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * 获取MCP服务器状态
   */
  async getStatus() {
    return this.request('/status');
  }

  /**
   * 获取所有可用的MCP工具
   */
  async getTools() {
    return this.request('/tools');
  }

  /**
   * 调用MCP工具
   */
  async callTool(toolName, args = {}) {
    return this.request('/tools/call', {
      method: 'POST',
      body: JSON.stringify({
        name: toolName,
        arguments: args
      })
    });
  }

  /**
   * 初始化MCP连接
   */
  async initialize() {
    return this.request('/initialize', {
      method: 'POST',
      body: JSON.stringify({
        protocolVersion: '2024-11-05',
        capabilities: {},
        clientInfo: {
          name: 'emotion-app-client',
          version: '1.0.0'
        }
      })
    });
  }

  /**
   * 健康检查
   */
  async healthCheck() {
    return this.request('/health');
  }
}

/**
 * 情绪记录MCP客户端
 */
class EmotionRecordsMCPClient extends MCPClient {
  /**
   * 查询情绪记录
   */
  async queryEmotionRecords(query, options = {}) {
    const { 
      startDate, 
      endDate, 
      emotionTypes, 
      includeAnalysis = true, 
      limit = 50 
    } = options;

    return this.callTool('query_emotion_records', {
      userId: this.getCurrentUserId(),
      query,
      startDate,
      endDate,
      emotionTypes,
      includeAnalysis,
      limit
    });
  }

  /**
   * 获取情绪趋势数据
   */
  async getEmotionTrends(timeRange = '7d') {
    return this.queryEmotionRecords(`分析最近${timeRange}的情绪变化趋势`, {
      includeAnalysis: true
    });
  }

  /**
   * 获取情绪统计信息
   */
  async getEmotionStats(timeRange = '30d') {
    return this.queryEmotionRecords(`统计最近${timeRange}的情绪分布`, {
      includeAnalysis: true
    });
  }

  /**
   * 获取情绪模式分析
   */
  async getEmotionPatterns() {
    return this.queryEmotionRecords('分析我的情绪模式和规律', {
      includeAnalysis: true
    });
  }

  getCurrentUserId() {
    // 从localStorage或context获取当前用户ID
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.username || 'anonymous';
  }
}

/**
 * 聊天记录MCP客户端
 */
class ChatRecordsMCPClient extends MCPClient {
  /**
   * 查询聊天记录
   */
  async queryChatRecords(query, options = {}) {
    const { 
      sessionIds,
      startDate, 
      endDate, 
      includeAnalysis = true,
      analysisType = 'summary',
      limit = 100 
    } = options;

    return this.callTool('query_chat_records', {
      userId: this.getCurrentUserId(),
      query,
      sessionIds,
      startDate,
      endDate,
      includeAnalysis,
      analysisType,
      limit
    });
  }

  /**
   * 生成聊天摘要
   */
  async generateChatSummary(sessionIds = null, timeRange = '7d') {
    const query = sessionIds ? 
      '总结这些对话的主要内容' : 
      `总结最近${timeRange}的聊天记录`;
      
    return this.queryChatRecords(query, {
      sessionIds: sessionIds?.join(','),
      analysisType: 'summary',
      includeAnalysis: true
    });
  }

  /**
   * 提取聊天主题
   */
  async extractChatTopics(timeRange = '30d') {
    return this.queryChatRecords(`提取最近${timeRange}聊天的主要话题`, {
      analysisType: 'topics',
      includeAnalysis: true
    });
  }

  /**
   * 分析聊天情感
   */
  async analyzeChatSentiment(timeRange = '30d') {
    return this.queryChatRecords(`分析最近${timeRange}聊天的情感倾向`, {
      analysisType: 'sentiment',
      includeAnalysis: true
    });
  }

  /**
   * 获取关键对话要点
   */
  async getKeyPoints(timeRange = '30d') {
    return this.queryChatRecords(`提取最近${timeRange}对话的关键要点`, {
      analysisType: 'keyPoints',
      includeAnalysis: true
    });
  }

  /**
   * 获取行动项
   */
  async getActionItems(timeRange = '30d') {
    return this.queryChatRecords(`识别最近${timeRange}对话中的行动项和决定`, {
      analysisType: 'actionItems',
      includeAnalysis: true
    });
  }

  getCurrentUserId() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.username || 'anonymous';
  }
}

// 创建单例实例
export const mcpClient = new MCPClient();
export const emotionMCPClient = new EmotionRecordsMCPClient();
export const chatMCPClient = new ChatRecordsMCPClient();

// 默认导出
export default {
  mcpClient,
  emotionMCPClient,
  chatMCPClient
};